import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ExportPanel = ({ onExport }) => {
  const [exportingType, setExportingType] = useState(null);

  const exportOptions = [
    {
      id: 'pdf',
      name: 'PDF Report',
      description: 'Generate comprehensive PDF report with all charts and insights',
      icon: 'FileText',
      color: 'error'
    },
    {
      id: 'powerbi',
      name: 'Power BI',
      description: 'Export data to Power BI for advanced analytics',
      icon: 'BarChart3',
      color: 'warning'
    },
    {
      id: 'sheets',
      name: 'Google Sheets',
      description: 'Export raw data to Google Sheets for collaboration',
      icon: 'Sheet',
      color: 'success'
    },
    {
      id: 'excel',
      name: 'Excel',
      description: 'Download data as Excel spreadsheet',
      icon: 'FileSpreadsheet',
      color: 'primary'
    }
  ];

  const handleExport = async (type) => {
    setExportingType(type);
    try {
      await onExport(type);
      // Simulate export delay
      setTimeout(() => {
        setExportingType(null);
      }, 2000);
    } catch (error) {
      console.error('Export failed:', error);
      setExportingType(null);
    }
  };

  const getButtonVariant = (color) => {
    switch (color) {
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'error': return 'destructive';
      default: return 'default';
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <div className="flex items-center space-x-2 mb-4">
        <Icon name="Download" size={20} className="text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground">Export Data</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {exportOptions?.map((option) => (
          <div
            key={option?.id}
            className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-colors duration-200"
          >
            <div className="mb-3">
              <div className={`w-8 h-8 rounded-lg bg-${option?.color}/10 flex items-center justify-center inline-flex`}>
                <Icon 
                  name={option?.icon} 
                  size={16} 
                  color={`var(--color-${option?.color})`}
                />
              </div>
            </div>
            
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-foreground mb-1 leading-tight">
                {option?.name}
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {option?.description}
              </p>
            </div>
            
            <div className="mt-auto">
              <Button
                variant={getButtonVariant(option?.color)}
                size="sm"
                onClick={() => handleExport(option?.id)}
                loading={exportingType === option?.id}
                disabled={exportingType && exportingType !== option?.id}
                iconName={exportingType === option?.id ? undefined : "Download"}
                iconPosition="left"
                fullWidth
                className="min-h-[36px] px-4 text-sm font-medium"
              >
                {exportingType === option?.id ? 'Exporting...' : 'Export'}
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 p-3 bg-muted/50 rounded-lg">
        <div className="flex items-start space-x-2">
          <Icon name="Info" size={16} className="text-muted-foreground mt-0.5" />
          <div>
            <p className="text-xs text-muted-foreground">
              <strong>Export includes:</strong> All filtered data, charts, summary metrics, and analysis insights. 
              Large datasets may take a few moments to process.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;